<template>
  <div class="cal-view" :style="theme" v-show="!loading">
    <!-- One block per event. Collapsed = compact selector, expanded = two-column card. -->
    <div
      v-for="(game, idx) in safeGames"
      :key="'cal-' + (game.game_id || game.id || idx)"
      class="cal-event"
      :class="{ 'is-open': isSelected(game) }"
    >
      <!-- Collapsed selector -->
      <button v-if="!isSelected(game)" type="button" class="cal-pick" @click="selectGame(game)">
        <span class="cal-pick__thumb">
          <img v-if="gameImage(game)" v-twin-src="gameImage(game)" :alt="game.game_name" :style="{ 'object-fit': gameImageFit(game) }" />
          <i v-else class="tim-icons icon-controller"></i>
        </span>
        <span class="cal-pick__meta">
          <span v-if="bss.show_game_title" class="cal-pick__name">{{ game.game_name }}</span>
          <span v-if="metaLine(game)" class="cal-pick__sub">{{ metaLine(game) }}</span>
        </span>
        <span class="cal-pick__cta">
          <span class="cal-pick__cta-text">Book</span>
          <i class="tim-icons icon-minimal-right"></i>
        </span>
      </button>

      <!-- Expanded two-column card -->
      <div v-else class="cal-card">
        <aside class="cal-card__aside">
          <div class="cal-card__poster">
            <img v-if="gameImage(game)" v-twin-src="gameImage(game)" :alt="game.game_name" :style="{ 'object-fit': gameImageFit(game) }" />
            <i v-else class="tim-icons icon-controller"></i>
            <span class="cal-card__poster-scrim"></span>
          </div>
          <div class="cal-card__head">
            <h4 v-if="bss.show_game_title" class="cal-card__name">{{ game.game_name }}</h4>
            <button type="button" class="cal-card__collapse" aria-label="Collapse" @click="toggleGame(game)">
              <i class="tim-icons icon-minimal-up"></i>
            </button>
          </div>
          <div v-if="metaLine(game)" class="cal-card__sub">{{ metaLine(game) }}</div>
          <div
            v-if="bss.show_game_description && game.game_description"
            class="cal-card__desc"
            v-html="game.game_description"
          ></div>
        </aside>

        <div class="cal-card__main">
          <!-- Calendar -->
          <div class="cal-block-label">Select date</div>
          <div class="cal-nav">
            <button type="button" class="cal-nav__btn" :disabled="!canGoPrev" aria-label="Previous month" @click="changeMonth(-1)">
              <i class="tim-icons icon-minimal-left"></i>
            </button>
            <span class="cal-nav__label" aria-live="polite">{{ monthLabel }}</span>
            <button type="button" class="cal-nav__btn" aria-label="Next month" @click="changeMonth(1)">
              <i class="tim-icons icon-minimal-right"></i>
            </button>
          </div>

          <div class="cal-dow" aria-hidden="true">
            <span v-for="(d, i) in weekdayLetters" :key="'wd-' + i">{{ d }}</span>
          </div>

          <div
            class="cal-grid-wrap"
            role="grid"
            aria-label="Choose a date"
            @keydown="onGridKeydown"
          >
            <!-- Loading skeleton -->
            <div v-if="isMonthLoading || scanning" class="cal-grid">
              <div v-for="w in 5" :key="'skw-' + w" class="cal-week">
                <span v-for="n in 7" :key="'sk-' + w + '-' + n" class="cal-day cal-day--skeleton"></span>
              </div>
            </div>
            <!-- Day grid -->
            <div v-else class="cal-grid" :key="cacheKey">
              <div v-for="(week, wi) in gridWeeks" :key="'w-' + wi" class="cal-week" role="row">
                <template v-for="(cell, ci) in week">
                  <span v-if="cell.blank" :key="'b-' + wi + '-' + ci" class="cal-day cal-day--blank" role="presentation"></span>
                  <button
                    v-else
                    :ref="'day-' + cell.date"
                    :key="'d-' + cell.date"
                    type="button"
                    class="cal-day"
                    role="gridcell"
                    :class="dayClass(cell)"
                    :tabindex="cell.date === rovingDate ? 0 : -1"
                    :aria-label="dayAria(cell)"
                    :aria-selected="selectedDate === cell.date ? 'true' : 'false'"
                    :aria-disabled="!isDaySelectable(cell) ? 'true' : 'false'"
                    :disabled="!isDaySelectable(cell)"
                    @click="selectDate(cell.date)"
                  >
                    <span class="cal-day__num">{{ cell.day }}</span>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- Empty month: offer to jump to the next month that has availability -->
          <div v-if="!isMonthLoading && !scanning && monthIsEmpty" class="cal-empty">
            <span class="cal-empty__text">No times available in {{ monthLabel }}.</span>
            <button type="button" class="cal-empty__btn" @click="jumpToNextAvailable">Find next available</button>
          </div>

          <!-- Time slots -->
          <transition name="cal-reveal">
            <div v-if="selectedDate" class="cal-times">
              <div class="cal-block-label">Select time</div>
              <div class="cal-times__date">{{ selectedDateLabel }}</div>

              <div v-if="selectedDaySlots.length === 0" class="cal-times__empty">
                {{ $t('book.noBookings') }}
              </div>

              <div v-else class="cal-slot-grid">
                <button
                  v-for="(s, si) in selectedDaySlots"
                  :key="selectedDate + '-ts-' + si"
                  type="button"
                  class="cal-slot"
                  :class="slotClass(s)"
                  :style="[slotStyle(s), { animationDelay: Math.min(si, 11) * 28 + 'ms' }]"
                  :disabled="slotDisabled(s)"
                  @click="onSlotClick(game, s)"
                >
                  <span v-if="badge(s)" class="cal-slot__badge" :style="badgeStyle">{{ s.custom_message }}</span>
                  <span class="cal-slot__time">{{ getDisplayTime(s) }}</span>
                  <span v-if="ticketCount(s)" class="cal-slot__sub">
                    {{ remaining(game, s) }}/{{ game.max_players_count }} {{ $t('book.availableTickets') }}
                  </span>
                  <span v-if="slotLabel(s)" class="cal-slot__sub">{{ slotLabel(s) }}</span>
                  <span v-if="customText(s)" class="cal-slot__sub cal-slot__sub--custom">{{ s.custom_message }}</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Gift card row -->
    <div v-if="isGiftCard" class="cal-event">
      <button type="button" class="cal-pick" @click="$emit('gift-card-select')">
        <span class="cal-pick__thumb">
          <img v-if="giftCardImage" :src="giftCardImage" alt="Gift card" />
          <i v-else class="tim-icons icon-gift-2"></i>
        </span>
        <span class="cal-pick__meta">
          <span class="cal-pick__name">{{ (giftCardSettings && giftCardSettings.title) || 'Gift cards' }}</span>
          <span v-if="giftCardSettings && giftCardSettings.subtext" class="cal-pick__sub">{{ giftCardSettings.subtext }}</span>
        </span>
        <span class="cal-pick__cta">
          <span class="cal-pick__cta-text">{{ (giftCardSettings && giftCardSettings.button_text) || $t('book.buttons.select') }}</span>
          <i class="tim-icons icon-minimal-right"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_LOCATION } from '@/api-config'
import { time } from '@/plugins/dateFormatter'
import { buildBookingTheme } from '@/util/bookingTheme'
import {
  normalizeSlotArray,
  getSlotType,
  isSlotBookable,
  isSlotDisabled,
  getSlotLabel,
  shouldShowSlotBadge,
  shouldUseCustomText,
  showTicketCount,
  getRemainingTickets
} from '@/util/slotDisplay'
import { syncServerNow, getNow } from '@/util/serverTime'
import { parseGameImages, MOBILE_BREAKPOINT } from '@/util/gameImages'
import gameImageOrientation from '@/mixins/gameImageOrientation'

const WEEKDAY_BASE = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const WEEKDAY_BASE_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const STATUS_ARIA = {
  available: 'available',
  'call-to-book': 'available by phone',
  'sold-out': 'sold out',
  none: 'no times',
  past: 'unavailable'
}
const MAX_MONTH_SCAN = 14

function pad(n) {
  return (n < 10 ? '0' : '') + n
}

export default {
  name: 'EventSelectionCalendar',
  mixins: [gameImageOrientation],
  props: {
    games: { type: [Array, String], default: () => [] },
    bss: { type: Object, default: () => ({}) },
    locale: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    isGiftCard: { type: Boolean, default: false },
    giftCardLink: { type: Boolean, default: false },
    giftCardSettings: { type: Object, default: () => ({}) },
    giftCardImage: { type: String, default: null }
  },
  data() {
    const now = getNow()
    return {
      selectedGameId: null,
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(), // 0-based
      selectedDate: null,
      focusedDate: null, // roving-tabindex target for keyboard nav
      slideDir: 'next',
      scanning: false,
      windowWidth: window.innerWidth,
      // cache keyed `${gameId}:${year}-${month}` -> { loading, byDate, statusByDate, firstAvailable }
      monthCache: {}
    }
  },
  mounted() {
    this._onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  computed: {
    theme() {
      return buildBookingTheme(this.bss)
    },
    safeGames() {
      if (this.giftCardLink) return []
      return Array.isArray(this.games) ? this.games : []
    },
    selectedGame() {
      return this.safeGames.find(g => this.gameId(g) === this.selectedGameId) || null
    },
    firstDayOfWeek() {
      const v = this.locale.first_day_of_week
      return typeof v === 'number' ? v : 0
    },
    weekdayLetters() {
      const out = []
      for (let i = 0; i < 7; i++) {
        out.push(WEEKDAY_BASE[(this.firstDayOfWeek + i) % 7])
      }
      return out
    },
    monthLabel() {
      return MONTHS[this.viewMonth] + ' ' + this.viewYear
    },
    cacheKey() {
      return this.selectedGameId + ':' + this.viewYear + '-' + this.viewMonth
    },
    currentMonthEntry() {
      return this.monthCache[this.cacheKey] || null
    },
    isMonthLoading() {
      return !this.currentMonthEntry || this.currentMonthEntry.loading
    },
    monthIsEmpty() {
      const e = this.currentMonthEntry
      return !!e && !e.loading && Object.keys(e.byDate).length === 0
    },
    canGoPrev() {
      const now = getNow()
      if (this.viewYear < now.getFullYear()) return false
      if (this.viewYear === now.getFullYear() && this.viewMonth <= now.getMonth()) return false
      return true
    },
    gridWeeks() {
      const cells = []
      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1)
      const startWeekday = firstOfMonth.getDay()
      const leading = (startWeekday - this.firstDayOfWeek + 7) % 7
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()
      for (let i = 0; i < leading; i++) cells.push({ blank: true })
      for (let d = 1; d <= daysInMonth; d++) {
        cells.push({
          blank: false,
          day: d,
          date: this.viewYear + '-' + pad(this.viewMonth + 1) + '-' + pad(d)
        })
      }
      while (cells.length % 7 !== 0) cells.push({ blank: true })
      const weeks = []
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
      return weeks
    },
    rovingDate() {
      if (this.focusedDate && this.focusedDate.indexOf(this.viewYear + '-' + pad(this.viewMonth + 1)) === 0) {
        return this.focusedDate
      }
      if (this.selectedDate) return this.selectedDate
      const e = this.currentMonthEntry
      if (e && e.firstAvailable) return e.firstAvailable
      return this.firstSelectableInView()
    },
    selectedDaySlots() {
      const entry = this.currentMonthEntry
      if (!entry || !this.selectedDate) return []
      return entry.byDate[this.selectedDate] || []
    },
    selectedDateLabel() {
      if (!this.selectedDate) return ''
      const d = this.dateToObj(this.selectedDate)
      return WEEKDAY_BASE_FULL[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate()
    },
    badgeStyle() {
      return { background: this.bss.button_color, color: this.bss.background_color || '#fff' }
    }
  },
  watch: {
    safeGames: {
      immediate: true,
      handler(list) {
        // Auto-open the only event so a single-game venue lands straight on the calendar.
        if (!this.selectedGameId && list && list.length === 1) {
          this.$nextTick(() => this.selectGame(list[0]))
        }
        if (Array.isArray(list)) {
          list.forEach(g => this.probeImageOrientation(parseGameImages(g.game_images, this.windowWidth <= MOBILE_BREAKPOINT)))
        }
      }
    }
  },
  methods: {
    gameId(game) {
      return game.game_id || game.id
    },
    isSelected(game) {
      return this.gameId(game) === this.selectedGameId
    },
    firstGameImage(game) {
      const images = parseGameImages(game.game_images, this.windowWidth <= MOBILE_BREAKPOINT)
      return images.length > 0 ? images[0] : null
    },
    gameImage(game) {
      const first = this.firstGameImage(game)
      return first ? first.url : null
    },
    // Letterbox a baked portrait poster in the fixed thumbnail; undefined lets
    // the component's CSS object-fit apply for landscape/legacy images.
    gameImageFit(game) {
      return this.imageFitInBox(this.firstGameImage(game))
    },
    metaLine(game) {
      const parts = []
      if (this.bss.show_number_of_players && game.max_players_count) {
        const min = game.min_players_count || 1
        parts.push(min + (game.max_players_count ? '-' + game.max_players_count : '') + ' ' + this.$t('book.groupSize').toLowerCase())
      }
      if (this.bss.show_game_duration && game.duration_minutes) {
        parts.push(game.duration_minutes + ' ' + this.$t('book.minutes'))
      }
      return parts.join('  ·  ')
    },
    dateToObj(dateStr) {
      const parts = dateStr.split('-')
      return new Date(parts[0], parts[1] - 1, parts[2])
    },
    dateStr(d) {
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    },
    toggleGame(game) {
      if (this.isSelected(game)) {
        this.selectedGameId = null
        this.selectedDate = null
        this.focusedDate = null
      } else {
        this.selectGame(game)
      }
    },
    async selectGame(game) {
      const now = getNow()
      this.selectedGameId = this.gameId(game)
      this.selectedDate = null
      this.focusedDate = null
      this.slideDir = 'next'
      this.viewYear = now.getFullYear()
      this.viewMonth = now.getMonth()
      await this.ensureMonth(game, this.viewYear, this.viewMonth)
      if (!this.currentMonthEntry || !this.currentMonthEntry.firstAvailable) {
        await this.jumpToNextAvailable()
      }
    },
    changeMonth(delta) {
      let m = this.viewMonth + delta
      let y = this.viewYear
      if (m < 0) { m = 11; y-- } else if (m > 11) { m = 0; y++ }
      this.slideDir = delta > 0 ? 'next' : 'prev'
      this.viewMonth = m
      this.viewYear = y
      this.selectedDate = null
      this.focusedDate = null
      if (this.selectedGame) this.ensureMonth(this.selectedGame, y, m)
    },
    keyFor(gameId, year, month) {
      return gameId + ':' + year + '-' + month
    },
    async ensureMonth(game, year, month) {
      const key = this.keyFor(this.gameId(game), year, month)
      if (this.monthCache[key] && !this.monthCache[key].loading) {
        this.autoSelectFirst(key)
        return
      }
      this.$set(this.monthCache, key, { loading: true, byDate: {}, statusByDate: {}, firstAvailable: null })

      const from = year + '-' + pad(month + 1) + '-01'
      const lastDay = new Date(year, month + 1, 0).getDate()
      const to = year + '-' + pad(month + 1) + '-' + pad(lastDay)
      const dt = getNow()
      const today = dt.getFullYear() + '-' + pad(dt.getMonth() + 1) + '-' + pad(dt.getDate())

      const url = API_LOCATION + 'games/' + this.gameId(game)
        + '/bookings?from=' + from + '&to=' + to
        + '&time=' + dt.getHours() + ':' + dt.getMinutes()
        + '&date=' + today + '&include_capacity=1'

      try {
        const response = await axios.get(url)
        syncServerNow(response.data.server_now)
        const raw = (response.data.booking_slots || []).map(s => {
          // Bookings endpoint returns ISO booking_date; the shared logic expects YYYY-MM-DD.
          s.booking_date = String(s.booking_date).split('T')[0]
          return s
        })
        const ordered = normalizeSlotArray(raw, game, this.bss, getNow())
        const byDate = {}
        const statusByDate = {}
        let firstAvailable = null
        for (const s of ordered) {
          if (this.isPast(s.booking_date)) continue
          const type = getSlotType(game, this.bss, s, getNow())
          s._type = type
          if (!byDate[s.booking_date]) byDate[s.booking_date] = []
          byDate[s.booking_date].push(s)
          const prev = statusByDate[s.booking_date]
          if (isSlotBookable(type)) {
            statusByDate[s.booking_date] = 'available'
            if (!firstAvailable) firstAvailable = s.booking_date
          } else if (type === 'call-to-book' || type === 'call-to-book-link') {
            if (prev !== 'available') statusByDate[s.booking_date] = 'call-to-book'
          } else if (!prev) {
            statusByDate[s.booking_date] = 'sold-out'
          }
        }
        this.$set(this.monthCache, key, { loading: false, byDate, statusByDate, firstAvailable })
        this.autoSelectFirst(key)
      } catch (e) {
        this.$set(this.monthCache, key, { loading: false, byDate: {}, statusByDate: {}, firstAvailable: null })
      }
    },
    autoSelectFirst(key) {
      if (key !== this.cacheKey || this.selectedDate) return
      const entry = this.monthCache[key]
      if (entry && entry.firstAvailable) this.selectedDate = entry.firstAvailable
    },
    async jumpToNextAvailable() {
      if (!this.selectedGame) return
      this.scanning = true
      this.slideDir = 'next'
      let y = this.viewYear
      let m = this.viewMonth
      for (let i = 0; i < MAX_MONTH_SCAN; i++) {
        await this.ensureMonth(this.selectedGame, y, m)
        const entry = this.monthCache[this.keyFor(this.selectedGameId, y, m)]
        if (entry && entry.firstAvailable) {
          this.viewYear = y
          this.viewMonth = m
          this.selectedDate = entry.firstAvailable
          this.scanning = false
          return
        }
        m++
        if (m > 11) { m = 0; y++ }
      }
      this.scanning = false
    },
    dayStatus(dateStr) {
      const entry = this.currentMonthEntry
      if (!entry) return 'none'
      return entry.statusByDate[dateStr] || 'none'
    },
    isPast(dateStr) {
      const d = this.dateToObj(dateStr)
      const today = getNow()
      today.setHours(0, 0, 0, 0)
      return d < today
    },
    isToday(dateStr) {
      const dt = getNow()
      return dateStr === this.dateStr(dt)
    },
    isDaySelectable(cell) {
      if (this.isPast(cell.date)) return false
      const status = this.dayStatus(cell.date)
      return status === 'available' || status === 'call-to-book' || status === 'sold-out'
    },
    firstSelectableInView() {
      for (const week of this.gridWeeks) {
        for (const cell of week) {
          if (!cell.blank && this.isDaySelectable(cell)) return cell.date
        }
      }
      for (const week of this.gridWeeks) {
        for (const cell of week) {
          if (!cell.blank) return cell.date
        }
      }
      return null
    },
    dayClass(cell) {
      const status = this.dayStatus(cell.date)
      return {
        'cal-day--past': this.isPast(cell.date),
        'cal-day--today': this.isToday(cell.date),
        'cal-day--selected': this.selectedDate === cell.date,
        'cal-day--available': !this.isPast(cell.date) && status === 'available',
        'cal-day--ctb': !this.isPast(cell.date) && status === 'call-to-book',
        'cal-day--soldout': !this.isPast(cell.date) && status === 'sold-out',
        'cal-day--none': status === 'none'
      }
    },
    dayAria(cell) {
      const d = this.dateToObj(cell.date)
      const status = this.isPast(cell.date) ? 'past' : this.dayStatus(cell.date)
      const statusText = STATUS_ARIA[status] || ''
      const base = WEEKDAY_LONG[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate()
      return statusText ? base + ', ' + statusText : base
    },
    // --- keyboard navigation (roving tabindex) ---
    onGridKeydown(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown', 'Enter', ' ', 'Spacebar', 'Escape']
      if (keys.indexOf(e.key) === -1) return
      e.preventDefault()

      if (e.key === 'Escape') {
        if (this.selectedGame) this.toggleGame(this.selectedGame)
        return
      }
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        const target = this.focusedDate || this.rovingDate
        if (target && !this.isPast(target) && this.dayStatus(target) !== 'none') {
          this.selectDate(target)
        }
        return
      }

      const d = this.dateToObj(this.focusedDate || this.rovingDate)
      const offset = (d.getDay() - this.firstDayOfWeek + 7) % 7
      if (e.key === 'ArrowLeft') d.setDate(d.getDate() - 1)
      else if (e.key === 'ArrowRight') d.setDate(d.getDate() + 1)
      else if (e.key === 'ArrowUp') d.setDate(d.getDate() - 7)
      else if (e.key === 'ArrowDown') d.setDate(d.getDate() + 7)
      else if (e.key === 'Home') d.setDate(d.getDate() - offset)
      else if (e.key === 'End') d.setDate(d.getDate() + (6 - offset))
      else if (e.key === 'PageUp') d.setMonth(d.getMonth() - 1)
      else if (e.key === 'PageDown') d.setMonth(d.getMonth() + 1)
      this.moveFocusTo(d)
    },
    moveFocusTo(d) {
      const y = d.getFullYear()
      const m = d.getMonth()
      if (y !== this.viewYear || m !== this.viewMonth) {
        this.slideDir = new Date(y, m, 1) > new Date(this.viewYear, this.viewMonth, 1) ? 'next' : 'prev'
        this.viewYear = y
        this.viewMonth = m
        if (this.selectedGame) this.ensureMonth(this.selectedGame, y, m)
      }
      this.focusedDate = this.dateStr(d)
      this.$nextTick(() => {
        const ref = this.$refs['day-' + this.focusedDate]
        const node = Array.isArray(ref) ? ref[0] : ref
        if (node && node.focus) node.focus()
      })
    },
    // --- slot rendering (parity with EventSelectionList via slotDisplay) ---
    slotType(s) {
      return s._type || getSlotType(this.selectedGame, this.bss, s, getNow())
    },
    slotClass(s) {
      const type = this.slotType(s)
      const disabled = isSlotDisabled(type)
      return { 'cal-slot--active': !disabled, 'cal-slot--disabled': disabled }
    },
    slotStyle(s) {
      // Chrome (border/fill/states) is owned by the theme tokens in CSS; only the venue
      // font and the disabled dim come through inline.
      const style = { fontFamily: this.bss.font_family_card }
      if (isSlotDisabled(this.slotType(s))) style.opacity = '0.5'
      return style
    },
    slotDisabled(s) {
      return isSlotDisabled(this.slotType(s))
    },
    slotLabel(s) {
      return getSlotLabel(this.bss, this.slotType(s), s)
    },
    badge(s) {
      return shouldShowSlotBadge(this.bss, this.slotType(s), s)
    },
    customText(s) {
      return shouldUseCustomText(this.bss, this.slotType(s), s)
    },
    ticketCount(s) {
      return showTicketCount(this.bss, this.slotType(s))
    },
    remaining(game, s) {
      return getRemainingTickets(game, s)
    },
    getDisplayTime(s) {
      if (s.is_all_day) {
        return s.all_day_text || this.$t('book.allDay')
      }
      return time(this.locale.time_format, s.start_time)
    },
    selectDate(dateStr) {
      if (this.isPast(dateStr)) return
      this.selectedDate = dateStr
      this.focusedDate = dateStr
    },
    onSlotClick(game, s) {
      const type = this.slotType(s)
      if (type === 'call-to-book-link' && s.has_external_link) {
        const w = window.open(s.external_link, '_blank')
        if (w) w.focus()
        return
      }
      if (isSlotBookable(type)) {
        this.$emit('slot-selected', { game, slot: s })
      }
    }
  }
}
</script>

<style scoped>
.cal-view {
  width: 100%;
  padding: 0 10px;
}

/* Event card */
.cal-event {
  max-width: 880px;
  margin: 0 auto 16px;
  background: var(--cal-surface);
  border: 1px solid var(--cal-line);
  border-radius: 20px;
  box-shadow: 0 28px 64px -28px var(--cal-shadow);
  overflow: hidden;
}

/* Collapsed selector */
.cal-pick {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--cal-ink);
  transition: background 160ms ease;
}
.cal-pick:hover {
  background: var(--cal-lift);
}
.cal-pick__thumb {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 13px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cal-fill);
}
.cal-pick__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cal-pick__thumb i {
  font-size: 22px;
  color: var(--cal-ink-faint);
}
.cal-pick__meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cal-pick__name {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--cal-ink);
}
.cal-pick__sub {
  font-size: 12.5px;
  color: var(--cal-ink-faint);
}
.cal-pick__cta {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 999px;
  background: var(--cal-accent);
  color: var(--cal-accent-ink);
  font-size: 13px;
  font-weight: 600;
  transition: transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cal-pick:hover .cal-pick__cta {
  transform: translateX(2px);
}
.cal-pick__cta i {
  font-size: 13px;
}

/* Expanded two-column card */
.cal-card {
  display: flex;
}
.cal-card__aside {
  width: 296px;
  flex-shrink: 0;
  padding: 30px 28px;
  border-right: 1px solid var(--cal-line);
}
.cal-card__poster {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  min-height: 200px;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cal-fill);
  box-shadow: 0 10px 30px -12px var(--cal-shadow);
}
.cal-card__poster img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cal-card__poster i {
  font-size: 32px;
  color: var(--cal-ink-faint);
}
.cal-card__poster-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 55%, var(--cal-shadow));
  opacity: 0.5;
  pointer-events: none;
}
.cal-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
}
.cal-card__name {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: var(--cal-ink);
  line-height: 1.25;
}
.cal-card__collapse {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: 1px solid var(--cal-line);
  border-radius: 50%;
  background: transparent;
  color: var(--cal-ink-faint);
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
}
.cal-card__collapse:hover {
  background: var(--cal-lift);
  color: var(--cal-ink);
}
.cal-card__sub {
  font-size: 12.5px;
  color: var(--cal-ink-faint);
  margin-top: 8px;
}
.cal-card__desc {
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--cal-ink-faint);
  margin-top: 12px;
  max-height: 150px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(180deg, #000 68%, transparent);
  mask-image: linear-gradient(180deg, #000 68%, transparent);
}
.cal-card__desc ::v-deep img {
  max-width: 100%;
  height: auto;
}

.cal-card__main {
  flex: 1;
  min-width: 0;
  padding: 30px;
}

/* Section eyebrow with an accent tick */
.cal-block-label {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--cal-ink-faint);
  margin-bottom: 16px;
}
.cal-block-label::before {
  content: '';
  width: 16px;
  height: 2px;
  border-radius: 2px;
  background: var(--cal-accent);
}

/* Month nav */
.cal-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.cal-nav__label {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--cal-ink);
}
.cal-nav__btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--cal-line);
  background: transparent;
  color: var(--cal-ink-faint);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;
}
.cal-nav__btn:hover:not(:disabled) {
  background: var(--cal-lift);
  color: var(--cal-ink);
  border-color: var(--cal-accent-ring);
}
.cal-nav__btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

/* Weekday header + day grid */
.cal-dow {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
}
.cal-dow span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--cal-ink-ghost);
}
.cal-grid-wrap {
  overflow: hidden;
}
.cal-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: cal-grid-in 240ms ease both;
}
@keyframes cal-grid-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.cal-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

/* Circular cells: the grid stays quiet; selection is the one filled element */
.cal-day {
  position: relative;
  height: 46px;
  border: none;
  background: transparent;
  color: var(--cal-ink);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cal-day::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0.6);
  background: var(--cal-fill);
  opacity: 0;
  z-index: 0;
  transition: opacity 140ms ease, transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cal-day__num {
  position: relative;
  z-index: 1;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  transition: color 140ms ease;
}
.cal-day--blank {
  cursor: default;
}
.cal-day--available:hover::before {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}
.cal-day:focus {
  outline: none;
}
.cal-day:focus-visible::before {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  background: transparent;
  box-shadow: inset 0 0 0 2px var(--cal-accent);
}
/* Availability dot under bookable days */
.cal-day--available::after,
.cal-day--ctb::after {
  content: '';
  position: absolute;
  bottom: 6px;
  left: 50%;
  margin-left: -2px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--cal-accent);
  z-index: 1;
}
/* Today: accent number + ring */
.cal-day--today .cal-day__num {
  color: var(--cal-accent);
}
.cal-day--today::before {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  background: transparent;
  box-shadow: inset 0 0 0 1.5px var(--cal-accent-ring);
}
/* Selected: filled accent circle that springs in */
.cal-day--selected::before {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  background: var(--cal-accent);
  box-shadow: 0 8px 18px -6px var(--cal-accent-soft);
  animation: cal-pop 240ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cal-day--selected .cal-day__num {
  color: var(--cal-accent-ink);
}
.cal-day--selected::after {
  display: none;
}
@keyframes cal-pop {
  from { transform: translate(-50%, -50%) scale(0.5); }
  to { transform: translate(-50%, -50%) scale(1); }
}
/* Call-to-book: faint, still tappable */
.cal-day--ctb .cal-day__num {
  color: var(--cal-ink-faint);
}
/* Sold out: ghost number, thin strike */
.cal-day--soldout .cal-day__num {
  color: var(--cal-ink-ghost);
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}
/* Past / no availability: receded, inert */
.cal-day--past,
.cal-day--none {
  cursor: default;
}
.cal-day--past .cal-day__num,
.cal-day--none .cal-day__num {
  color: var(--cal-ink-ghost);
}
.cal-day--skeleton {
  height: 46px;
}
.cal-day--skeleton::before {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  background: var(--cal-fill);
  animation: cal-pulse 1.3s ease-in-out infinite;
}
@keyframes cal-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.8; }
}

/* Empty-month notice */
.cal-empty {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--cal-line);
}
.cal-empty__text {
  font-size: 13px;
  color: var(--cal-ink-faint);
}
.cal-empty__btn {
  padding: 9px 16px;
  border-radius: 999px;
  border: none;
  background: var(--cal-accent);
  color: var(--cal-accent-ink);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/* Time slots */
.cal-times {
  margin-top: 30px;
}
.cal-times__date {
  font-size: 14px;
  font-weight: 600;
  color: var(--cal-ink);
  margin: -6px 0 14px;
}
.cal-times__empty {
  font-size: 13px;
  color: var(--cal-ink-faint);
  padding: 6px 0;
}
.cal-slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 10px;
}
.cal-slot {
  position: relative;
  background: transparent;
  border: 1px solid var(--cal-line);
  border-radius: 13px;
  padding: 12px 10px;
  min-height: 56px;
  cursor: pointer;
  color: var(--cal-ink);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.3;
  transition: transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
  animation: cal-slot-in 380ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
}
@keyframes cal-slot-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
.cal-slot--active:hover {
  border-color: var(--cal-accent);
  background: var(--cal-lift);
  transform: translateY(-2px);
  box-shadow: 0 10px 22px -10px var(--cal-shadow);
}
.cal-slot--disabled {
  cursor: default;
  color: var(--cal-ink-ghost);
}
.cal-slot__time {
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
}
.cal-slot__sub {
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 3px;
  color: var(--cal-ink-faint);
}
.cal-slot__sub--custom {
  text-transform: none;
  font-weight: 500;
  word-break: normal;
}
.cal-slot__badge {
  position: absolute;
  top: -8px;
  right: 8px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  max-width: calc(100% - 12px);
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  box-shadow: 0 2px 6px -1px var(--cal-shadow);
}

/* Time-slot reveal */
.cal-reveal-enter-active {
  transition: max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out;
  overflow: hidden;
}
.cal-reveal-leave-active {
  transition: max-height 200ms ease-in, opacity 150ms ease-in;
  overflow: hidden;
}
.cal-reveal-enter,
.cal-reveal-leave-to {
  max-height: 0;
  opacity: 0;
}
.cal-reveal-enter-to,
.cal-reveal-leave {
  max-height: 1400px;
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .cal-grid,
  .cal-slot,
  .cal-day--selected::before,
  .cal-reveal-enter-active,
  .cal-reveal-leave-active {
    animation: none !important;
    transition: opacity 120ms ease !important;
  }
  .cal-slot--active:hover {
    transform: none;
  }
  .cal-day::before {
    transition: opacity 120ms ease !important;
  }
}

/* Mobile: stack the two columns; keep the chosen date visible while scanning times */
@media (max-width: 700px) {
  .cal-card {
    flex-direction: column;
  }
  .cal-card__aside {
    width: 100%;
    padding: 18px;
    border-right: none;
    border-bottom: 1px solid var(--cal-line);
  }
  .cal-card__poster {
    aspect-ratio: 16 / 9;
    min-height: 0;
    max-height: 200px;
  }
  .cal-card__main {
    padding: 22px 18px;
  }
  .cal-day {
    height: 44px;
  }
  .cal-slot-grid {
    grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  }
  .cal-times__date {
    position: sticky;
    top: 0;
    z-index: 2;
    margin: 0 -18px 12px;
    padding: 12px 18px;
    background: var(--cal-surface);
    box-shadow: 0 4px 12px -6px var(--cal-shadow);
  }
}
</style>
